// ═══════════════════════════════════════════════════════════════════
// MODELOS PARA REQUESTS
// ═══════════════════════════════════════════════════════════════════

export type PasskeyPhase = 'check' | 'options' | 'authenticator' | 'verification';
export type PasskeyAvailabilityState = 'idle' | 'checking' | 'available' | 'absent' | 'error';
export type PasskeyFailureCode =
  | 'TIMEOUT' | 'CANCELLED' | 'NOT_ALLOWED' | 'NETWORK'
  | 'AUTH_REJECTED' | 'SERVER' | 'UNSUPPORTED' | 'SECURITY'
  | 'INVALID_RESPONSE' | 'BUSY' | 'UNKNOWN';

export class PasskeyOperationError extends Error {
  constructor(
    public readonly code: PasskeyFailureCode,
    public readonly phase: PasskeyPhase,
    public readonly originalName: string,
    public readonly status?: number,
    options?: ErrorOptions
  ) {
    super(code, options);
    this.name = 'PasskeyOperationError';
  }
}

export interface PasskeyOperationOptions {
  signal?: AbortSignal;
  deadlineAt?: number;
}

/**
 * Request para solicitar las opciones de registro de un nuevo Passkey.
 * 
 * Este es el primer paso del flujo de registro. El backend generará un challenge
 * único y devolverá las opciones necesarias para crear la credencial.
 * 
 * @property email - Email del usuario que registrará el passkey
 * @property partner - ID del partner/organización (ej: 1 para Billcentrix)
 * @property deviceName - Nombre opcional del dispositivo (ej: "iPhone 14 de Pedro")
 * 
 * @example
 * const request: PasskeyRegistrationOptionsRequest = {
 *   email: 'pedro@ejemplo.com',
 *   partner: 1,
 *   deviceName: 'iPhone 14 de Pedro'
 * };
 */
export interface PasskeyRegistrationOptionsRequest {
  email: string;
  partner: number;
  deviceName?: string;
}

/**
 * Request para completar el registro de un Passkey después de la creación exitosa.
 * 
 * Este es el segundo paso del flujo de registro. Contiene la credencial generada
 * por el navegador/dispositivo que debe ser verificada y almacenada en el backend.
 * 
 * @property credentialId - ID único de la credencial generada (base64url)
 * @property clientDataJSON - Datos del cliente en formato JSON (base64url)
 * @property attestationObject - Objeto de attestación que contiene la clave pública (base64url)
 * @property transports - Métodos de transporte soportados (ej: ['internal', 'usb'])
 * @property deviceName - Nombre opcional del dispositivo para identificación
 * 
 * @example
 * const request: PasskeyRegistrationRequest = {
 *   credentialId: 'xyz789...',
 *   clientDataJSON: 'eyJ0eXBlIjoid2ViYXV0aG4uY3JlYXRlIi...',
 *   attestationObject: 'o2NmbXRkbm9uZWdhdHRTdG10oGhhdXRoRGF...',
 *   transports: ['internal'],
 *   deviceName: 'iPhone 14 de Pedro'
 * };
 */
export interface PasskeyRegistrationRequest {
  credentialId: string;
  clientDataJSON: string;
  attestationObject: string;
  transports: string[];
  deviceName?: string;
}

/**
 * Request para solicitar las opciones de autenticación con Passkey.
 * 
 * Este es el primer paso del flujo de login. El backend generará un challenge
 * y devolverá las credenciales permitidas para este usuario.
 * 
 * @property email - Email del usuario que intentará autenticarse
 * @property partner - ID del partner/organización
 * 
 * @example
 * const request: PasskeyAuthenticationOptionsRequest = {
 *   email: 'pedro@ejemplo.com',
 *   partner: 1
 * };
 */
export interface PasskeyAuthenticationOptionsRequest {
  email: string;
  partner: number;
}

/**
 * Request para completar la autenticación con Passkey.
 * 
 * Este es el segundo paso del flujo de login o re-validación. Contiene la firma
 * criptográfica que prueba que el usuario posee la clave privada del passkey.
 * 
 * Se usa tanto para:
 * - Login inicial (POST /auth/passkey/login)
 * - Re-validación periódica (POST /auth/passkey/revalidate)
 * 
 * @property credentialId - ID de la credencial utilizada para autenticar
 * @property clientDataJSON - Datos del cliente en formato JSON (base64url)
 * @property authenticatorData - Datos del autenticador (base64url)
 * @property signature - Firma criptográfica que verifica la autenticidad (base64url)
 * 
 * @example
 * const request: PasskeyAuthenticationRequest = {
 *   credentialId: 'xyz789...',
 *   clientDataJSON: 'eyJ0eXBlIjoid2ViYXV0aG4uZ2V0Ii...',
 *   authenticatorData: 'SZYN5YgOjGh0NBcPZHZgW4...',
 *   signature: 'MEUCIQDxl+9...'
 * };
 */
export interface PasskeyAuthenticationRequest {
  credentialId: string;
  clientDataJSON: string;
  authenticatorData: string;
  signature: string;
}

// ═══════════════════════════════════════════════════════════════════
// MODELOS PARA RESPONSES
// ═══════════════════════════════════════════════════════════════════

/**
 * Response con las opciones necesarias para crear un nuevo Passkey.
 * 
 * Contiene toda la información que el navegador necesita para invocar
 * navigator.credentials.create() y generar una nueva credencial.
 * 
 * @property challenge - Desafío único generado por el servidor (base64url)
 * @property rp - Información del Relying Party (nuestra aplicación)
 * @property user - Información del usuario que registra el passkey
 * @property pubKeyCredParams - Algoritmos de clave p��blica soportados
 * @property timeout - Tiempo máximo en ms para completar el registro (60000 = 1 min)
 * @property excludeCredentials - Credenciales existentes a excluir (evita duplicados)
 * @property authenticatorSelection - Tipo de autenticador preferido ('platform' = biométrico)
 * @property attestation - Nivel de attestación requerido ('none' = sin verificación adicional)
 */
export interface PasskeyRegistrationOptionsResponse {
  challenge: string;
  rp: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: Array<{
    type: string;
    alg: number;
  }>;
  timeout: number;
  excludeCredentials?: Array<{
    type: string;
    id: string;
    transports?: string[];
  }>;
  authenticatorSelection: string;
  attestation: string;
}

/**
 * Response con las opciones necesarias para autenticar con Passkey.
 * 
 * Contiene la información que el navegador necesita para invocar
 * navigator.credentials.get() y obtener una firma del usuario.
 * 
 * @property challenge - Desafío único generado por el servidor (base64url)
 * @property timeout - Tiempo máximo en ms para completar la autenticación
 * @property rpId - ID del Relying Party (dominio de la aplicación)
 * @property allowCredentials - Lista de credenciales permitidas para este usuario
 * @property userVerification - Nivel de verificación requerido ('preferred' = biométrico si está disponible)
 */
export interface PasskeyAuthenticationOptionsResponse {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials: Array<{
    type: string;
    id: string;
    transports?: string[];
  }>;
  userVerification: string;
}

/**
 * Response después de completar exitosamente el registro de un Passkey.
 * 
 * Confirma que la credencial fue verificada y almacenada correctamente
 * en el backend.
 * 
 * @property success - Indica si el registro fue exitoso
 * @property message - Mensaje descriptivo del resultado
 * @property credentialId - ID de la credencial registrada
 * @property deviceName - Nombre del dispositivo registrado
 */
export interface PasskeyRegistrationResponse {
  success: boolean;
  message: string;
  credentialId: string;
  deviceName: string;
}

/**
 * Response después de completar exitosamente la autenticación con Passkey.
 * 
 * Contiene los tokens JWT necesarios para mantener la sesión del usuario.
 * Esta respuesta es idéntica a la del login tradicional, permitiendo
 * que el resto de la aplicación funcione sin cambios.
 * 
 * @property username - Email del usuario autenticado
 * @property message - Mensaje descriptivo del resultado
 * @property jwt - Token JWT de acceso (corta duración)
 * @property refreshToken - Token para renovar el JWT (larga duración)
 * @property status - Indica si la autenticación fue exitosa
 */
export interface PasskeyAuthenticationResponse {
  username: string;
  message: string;
  jwt: string;
  refreshToken: string;
  status: boolean;
}

/**
 * Response que indica si un usuario tiene Passkeys registrados.
 * 
 * Se usa para decidir si mostrar el botón de login con passkey
 * o solo el login tradicional.
 * 
 * @property hasPasskey - true si el usuario tiene al menos un passkey registrado
 */
export interface PasskeyCheckResponse {
  hasPasskey: boolean;
}

/**
 * Response después de completar exitosamente la re-validación con Passkey.
 * 
 * Confirma que el usuario se re-autenticó correctamente sin necesidad
 * de solicitar un PIN por email.
 * 
 * @property success - Indica si la re-validación fue exitosa
 * @property message - Mensaje descriptivo del resultado
 */
export interface PasskeyRevalidationResponse {
  success: boolean;
  message: string;
}

// ═══════════════════════════════════════════════════════════════════
// MODELOS DE ENTIDADES
// ═════════════════════════════════════════════════════��═════════════

/**
 * Representa un Passkey registrado en el sistema.
 * 
 * Esta entidad se usa para mostrar la lista de dispositivos del usuario
 * y permitir la gestión (eliminar passkeys antiguos o no utilizados).
 * 
 * @property id - ID interno de la base de datos
 * @property credentialId - ID único de la credencial (base64url)
 * @property deviceName - Nombre descriptivo del dispositivo (ej: "iPhone 14 de Pedro")
 * @property deviceType - Tipo de autenticador ('platform' = biométrico, 'cross-platform' = USB)
 * @property createdAt - Fecha de registro del passkey
 * @property lastUsedAt - Última vez que se usó este passkey (null si nunca se usó)
 * 
 * @example
 * const credential: PasskeyCredential = {
 *   id: 1,
 *   credentialId: 'xyz789...',
 *   deviceName: 'iPhone 14 de Pedro',
 *   deviceType: 'platform',
 *   createdAt: new Date('2024-01-15T10:30:00'),
 *   lastUsedAt: new Date('2024-01-20T15:30:00')
 * };
 */
export interface PasskeyCredential {
  id: number;
  credentialId: string;
  deviceName: string;
  deviceType: string;
  createdAt: Date;
  lastUsedAt: Date | null;
}

// ════════════════════════════════════���══════════════════════════════
// MODELOS PARA WEBAUTHN
// ═══════════════════════════════════════════════════════════════════

/**
 * NOTA: Para los tipos de WebAuthn (PublicKeyCredentialCreationOptionsJSON,
 * PublicKeyCredentialRequestOptionsJSON, etc.) usamos directamente los tipos
 * de @simplewebauthn/browser para garantizar compatibilidad total.
 * 
 * Importar así:
 * import type {
 *   PublicKeyCredentialCreationOptionsJSON,
 *   PublicKeyCredentialRequestOptionsJSON,
 *   RegistrationResponseJSON,
 *   AuthenticationResponseJSON
 * } from '@simplewebauthn/browser';
 * 
 * Esto evita problemas de tipos y garantiza que siempre estemos
 * sincronizados con la librería oficial.
 */
